import { useState, useEffect } from "react";
import { getCommitmentContent, DEFAULT_COMMITMENT } from "@/lib/content";
import type { CommitmentContent } from "@/types/content";
import { getOptimizedImageUrl } from "@/lib/utils";
import { Truck, CreditCard } from "lucide-react";

const SizeUpSection = () => {
  const [content, setContent] = useState<CommitmentContent>(DEFAULT_COMMITMENT);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    getCommitmentContent(setContent).then(setContent);
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <section id="commitment" className="relative flex flex-col md:block md:min-h-[800px] overflow-hidden bg-[#171717] border-t border-[#EAE5D9]/10">
      {/* Full Screen Background Image */}
      <div className="relative w-full aspect-[4/5] sm:aspect-square md:absolute md:inset-0 md:z-0 md:aspect-auto">
        <img
          src={getOptimizedImageUrl(content.bgImageUrl || "https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=1600&auto=format&fit=crop", isMobile)}
          alt="Our Commitment"
          width="1920"
          height="1080"
          className="w-full h-full object-cover opacity-70 mix-blend-luminosity"
          loading="lazy"
        />
        {/* Dark Overlay for Readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#171717] via-[#171717]/90 to-transparent md:bg-gradient-to-r md:from-[#171717] md:from-0% md:via-[#171717]/90 md:via-[20%] md:to-transparent md:to-[40%]" />
      </div>

      <div className="container px-4 md:px-8 relative z-10 py-16 md:py-24 flex items-center md:min-h-[800px]">
        <div className="max-w-2xl space-y-12">
          
          <div className="space-y-6">
            <p className="text-[#C4A47E] font-body font-light tracking-[0.4em] uppercase text-[9px] md:text-xs">
              {content.badge}
            </p>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-display font-light text-[#EAE5D9] uppercase tracking-[0.2em] leading-[1.2]">
              {content.titleLine1}<br/>
              <span className="text-[#C4A47E]">{content.titleHighlight}</span><br/>
              {content.titleLine2}
            </h2>
            <p className="text-[#EAE5D9]/70 text-xs sm:text-sm md:text-base font-body font-light leading-relaxed max-w-lg">
              {content.description}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-6 md:gap-12 pt-6 border-t border-[#EAE5D9]/10">
            {content.stats.map((stat) => (
              <div key={stat.label} className="space-y-2">
                <div className="text-xl md:text-3xl font-display font-light text-[#C4A47E] tracking-widest">{stat.value}</div>
                <div className="text-[#EAE5D9]/50 text-[9px] md:text-[10px] font-body font-light uppercase tracking-[0.25em] leading-relaxed">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Delivery & Payment Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 pt-4">
            <div className="flex items-center gap-4 bg-transparent border border-[#EAE5D9]/15 px-6 py-5 rounded-none group hover:border-[#C4A47E]/50 transition-colors duration-500">
              <Truck size={20} strokeWidth={1} className="text-[#C4A47E]" />
              <div>
                <div className="text-[#EAE5D9] font-display font-light text-xs md:text-sm uppercase tracking-[0.2em] mb-1">{content.deliveryTitle}</div>
                <div className="text-[#EAE5D9]/50 text-[9px] font-body font-light uppercase tracking-[0.15em]">{content.deliverySub}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-4 bg-transparent border border-[#EAE5D9]/15 px-6 py-5 rounded-none group hover:border-[#C4A47E]/50 transition-colors duration-500">
              <CreditCard size={20} strokeWidth={1} className="text-[#C4A47E]" />
              <div>
                <div className="text-[#EAE5D9] font-display font-light text-xs md:text-sm uppercase tracking-[0.2em] mb-1">{content.paymentTitle}</div>
                <div className="text-[#EAE5D9]/50 text-[9px] font-body font-light uppercase tracking-[0.15em]">{content.paymentSub}</div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default SizeUpSection;
